import { useState, useRef } from "react";
import VehicleChildForm from "./components/vehicleChildForm";
import DriverChildForm from "./components/driverChildForm";
import DealershipChildForm from "./components/dealershipChildForm";
import { requiredFieldsByStep } from "./utils/required-fields-by-step";
import ErrorDialog from "../../../../components/dialogs/ErrorDialog";
import ServiceChildForm from "./components/serviceChildForm";
import SuccessDialog from "../../../../components/dialogs/SuccessDialog";
import {
  OccurrenceStepEnum,
  OccurrenceStepLabelEnum,
} from "../../../../enums/occurrenceStep";
import { Occurrence } from "../../../../models/occurrence";

import { createServiceBaySchedule } from "../../../../api/serviceBay";

import { IForm } from "../..";
import { fieldsLabels } from "./utils/fields-labels";
import { FinalizeOccurrenceButton } from "../../dialogs/finalizationReasonDialog";
import { Asset } from "../../../../models/asset";
import SchedulingSummary from "./components/schedulingSummary";
import { scrollToFirstMissingField } from "./utils/scroll-to-missing-field";

const CallForm = (props: IForm) => {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const finalized = props.finalized ?? false;
  const loadings = props.loading ?? false;
  const disabledAllFields = props.disabledAllFields ?? false;
  const [hasError, setHasError] = useState(false);
  const [asset, setAsset] = useState<Asset | undefined>(undefined);
  const scheduling = props.scheduling;
  const formContainerRef = useRef<HTMLDivElement>(null);

  const [serviceBayScheduleWasUpdated, setServiceBayScheduleWasUpdated] =
    useState(false);

  const requiredServiceFields = props.occurrence?.occurrenceType === "P"
    ? requiredFieldsByStep.CALL_PREVENTIVE_CHECKUP
    : requiredFieldsByStep.CALL;

  return (
    <div ref={formContainerRef}>
      <div className="text-size-h4 font-bold">Chamado</div>
      <div className="text-color-status-resting text-size-14">
        Atribua os dados para relacionar a etapa de chamado
      </div>

      <SchedulingSummary 
        scheduling={scheduling}
        asset={asset}
        occurrence={props.occurrence}
        persona={props.persona}
      />

      <VehicleChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={["assetId"]}
        disabledFields={[]}
        requiredFields={requiredFieldsByStep.VEHICLE}
        disabledAllFields={disabledAllFields}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
        scheduling={props.scheduling}
      />

      <DriverChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[]}
        disabledFields={[]}
        requiredFields={requiredFieldsByStep.DRIVER}
        disabledAllFields={disabledAllFields}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <DealershipChildForm
        occurrence={props.occurrence}
        setOccurrence={props.setOccurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[]}
        disabledFields={["dealershipName", "dealershipDn"]}
        requiredFields={requiredFieldsByStep.DEALERSHIP}
        disabledAllFields={disabledAllFields}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <ServiceChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[
          "estimatedTime",
          "partOrderStatus",
          "osNumber",
          "osDtOpenAt",
          "driverCheckInDriver",
          "mechanicLocation",
          "towTruckLocation",
          "literatureDiagnosis",
          "literatureTroubleshooting",
          "tasNumber",
          "tasStatus",
          "checklist",
          "solutionProposed",
        ]}
        disabledFields={["createdBy", "dtStart", "dtEnd"]}
        requiredFields={requiredServiceFields}
        step={props.step}
        disabledAllFields={disabledAllFields}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        partners={props.partners}
        addDispatch={props.addDispatch}
        cancelDispatch={props.cancelDispatch}
        persona={props.persona}
        setServiceBayScheduleWasUpdated={setServiceBayScheduleWasUpdated}
        hasError={hasError}
        scheduling={props.scheduling}
      />

      <hr className="my-6" />

      {props.persona !== "MANAGER" && !finalized && !props.hiddenSaveButton && (
        <div className="flex justify-end gap-3">
          <button
            className={`btn btn-primary ${
              loadings ? "btn-loading disabled" : ""
            }`}
            type="button"
            onClick={async () => {
              if (!props.occurrence) return;
              
              const isValid = props.isValidStep(requiredServiceFields, props.occurrence);
              setHasError(!isValid);

              // Se inválido, rola até o primeiro campo faltante
              if (!isValid) {
                const container = formContainerRef.current?.closest('.kanban-sidebar') as HTMLElement;
                scrollToFirstMissingField(requiredServiceFields, props.occurrence, container);
                return;
              }
              
              const success = await props.save(props.occurrence);
              if (success) {
                if (
                  serviceBayScheduleWasUpdated &&
                  props.occurrence.serviceBaySchedule
                ) {
                  // TODO: se der erro, desfazer o update da occurrence
                  await createServiceBaySchedule({
                    ...props.occurrence.serviceBaySchedule,
                    occurrence:
                      props.occurrence.serviceBaySchedule.occurrence?.id,
                    serviceBay:
                      props.occurrence.serviceBaySchedule.serviceBay?.id,
                  });
                }
                setShowSuccessDialog(true);
              } else {
                setShowErrorDialog(true);
              }
            }}
            disabled={loadings || disabledAllFields || hasError}
          >
            Salvar
          </button>
          
          <FinalizeOccurrenceButton
            occurrence={props.occurrence} 
            finishOccurrence={props.finishOccurrence} 
            persona={props.persona}
            stepId={props.step?.id}
          />
        </div>
      )}

      {props.persona !== "MANAGER" &&
        !props.finalized &&
        props.hiddenSaveButton &&
        props.occurrence?.steps?.length === 0 && (
          <div className="flex justify-end">
            <button
              className={`btn btn-primary ${
                loadings ? "btn-loading disabled" : ""
              }`}
              type="button"
              onClick={async () => {
                if (!props.occurrence || !props.changeStep) return;
                const success = await props.changeStep(
                  props.occurrence,
                  -1,
                  OccurrenceStepEnum.CALL
                );
                if (success) {
                  setShowSuccessDialog(true);
                } else {
                  setShowErrorDialog(true);
                }
              }}
              disabled={loadings || disabledAllFields || hasError}
            >
              Abrir chamado
            </button>
          </div>
        )}

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Erro ao salvar"
        body="Ocorreu um erro ao salvar o chamado. Por favor, tente novamente ou entre em contato com o suporte."
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Chamado salvo com sucesso"
        footer={
          <>
            <button
              className="btn btn-primary"
              disabled={hasError || !props.changeStep || !props.occurrence || !props.summaryStep?.id}
              onClick={() => {
                props.summaryStep?.id && props.changeStep!(
                  props.occurrence!,
                  props.summaryStep.id,
                  OccurrenceStepEnum.SCREENING
                );
                setShowSuccessDialog(false);
                props.onClose();
              }}
            >
              Avançar etapa
            </button>
          </>
        }
        body={
          <div className="space-y-4">
            <div>O chamado foi salvo com sucesso.</div>
            {!hasError && (
              <div>
                Deseja avançar para a etapa {OccurrenceStepLabelEnum.SCREENING}?{" "}
              </div>
            )}
            {hasError && (
              <>
                <div>
                  É necessário preencher todos os campos obrigatórios para
                  avançar para a próxima etapa.
                </div>
                <div>Campos obrigatórios não preenchidos:</div>
                <ul className="list-disc list-inside">
                  {props
                    .getNotFilledRequiredFields(
                      requiredServiceFields,
                      props.occurrence ?? ({} as Occurrence)
                    )
                    .map((field: string) => (
                      <li key={field}>
                        {fieldsLabels[field as keyof typeof fieldsLabels]}
                      </li>
                    ))}
                </ul>
              </>
            )}
          </div>
        }
      />
    </div>
  );
};

export default CallForm;
